/* ============================================================
   viewport.js -- what kind of device is driving the page
   ------------------------------------------------------------
   FOX asks two separate questions, and conflating them is what
   produced most of its touch problems:

     "Is the pointer coarse?"  -> how big must a target be
     "Is the screen narrow?"   -> what layout fits

   They are genuinely independent. An iPad in landscape is
   1194pt wide, as wide as a laptop, and has no hover. A
   touchscreen laptop is wide AND coarse. A phone is narrow and
   coarse. Sizing rules key off the pointer; layout rules key
   off the width. Nothing here guesses one from the other.
   ============================================================ */
import React from "react";

/* The width below which FOX swaps the header menu for the bottom tab bar.
   Kept here rather than inline so the JS and the CSS cannot drift: the same
   number appears in App.css and MobileNav.css, and changing it means changing
   all three together. */
export const PHONE_MAX_WIDTH = 768;

export function useMediaQuery(query) {
  const [matches, setMatches] = React.useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const handleChange = (event) => setMatches(event.matches);
    // Re-read on mount as well as on change: the query can already differ from
    // the initial state if the window was resized before this effect ran.
    setMatches(mql.matches);
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, [query]);

  return matches;
}

/* True when the device has a real hovering pointer -- a mouse or a trackpad.
   Drives whether a menu may rely on hover to open. */
export function useCanHover() {
  return useMediaQuery("(hover: hover) and (pointer: fine)");
}

/* True on a phone-width screen, where the wrapping header menu does not fit
   and navigation moves to the bottom of the screen. */
export function useIsPhone() {
  return useMediaQuery(`(max-width: ${PHONE_MAX_WIDTH}px)`);
}

/* True on a touch device wide enough for the desktop layout -- an iPad, in
   other words -- where navigation moves to a persistent left rail.

   Two halves, and both are load-bearing:

     min-width      a 1280px laptop window is a desktop and keeps its header
                    menu. Width alone would have taken it away.

     any-pointer    NOT `pointer: coarse`. An iPad with a Magic Keyboard
                    attached reports its primary pointer as FINE, because the
                    trackpad takes over -- which is why the touch sizing
                    shipped earlier was invisible on exactly those iPads.
                    `any-pointer` asks whether any available pointer is
                    coarse, and an iPad has a touchscreen whatever is plugged
                    into it.

   The deliberate consequence is that a touchscreen laptop gets the rail. It
   is a touch device, so that is defensible; this query is the one place to
   change it if it turns out not to be. */
export function useIsTablet() {
  return useMediaQuery(`(min-width: ${PHONE_MAX_WIDTH + 1}px) and (any-pointer: coarse)`);
}

/* The site header's height, measured rather than assumed.

   #fox-header is sticky and its height is fractional -- rem padding plus
   content -- and it reflows at narrow widths. Anything that has to sit
   directly beneath it, like the rail, needs the real number or it ends up
   overlapped at one width and gapped at another. The same reasoning is
   already written out at length on usePinnedFilterBar in FilterBar.js. */
export function useHeaderOffset() {
  const [offset, setOffset] = React.useState(64);

  React.useEffect(() => {
    const header = document.getElementById("fox-header");
    if (!header) return undefined;
    const measure = () => setOffset(Math.floor(header.getBoundingClientRect().height));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  return offset;
}

/* Prevents the page behind a sheet or modal from scrolling while it is open.

   iOS needs the position/top treatment rather than `overflow: hidden` alone,
   which it ignores on the body; the saved offset is restored on close so the
   page does not jump back to the top when the sheet is dismissed. */
export function useBodyScrollLock(locked) {
  React.useEffect(() => {
    if (!locked) return undefined;
    const { body } = document;
    const scrollY = window.scrollY;
    const previous = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.width = previous.width;
      body.style.overflow = previous.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [locked]);
}
